import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../assets/guardianPlacement.css';
import Loader from '../components/Loader';

const GuardianPlacementStep2 = () => {
  const { propertyId, guardianId, roomId, secondGuardianId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [property, setProperty] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [secondGuardian, setSecondGuardian] = useState(null);
  const [room, setRoom] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  // Form state for couple assignment
  const [paymentSplit, setPaymentSplit] = useState('50:50');
  const [secondGuardianIdForm, setSecondGuardianIdForm] = useState(secondGuardianId || '');

  // Mock data for when API fails
  const getMockData = () => {
    const mockProperty = {
      id: parseInt(propertyId),
      property_id: "PROP001",
      name: "12 Savage Road",
      status_text: "Active",
      address_1: "12 Savage Road",
      postcode: "PL5 1BP"
    };

    const mockGuardian = {
      id: parseInt(guardianId),
      unique_guardian_number: "UGD001",
      full_name: "John Smith",
      status: "Documents Required",
      user: {
        mobile_number: "07123456789",
        email: "john.smith@example.com"
      }
    };

    const mockSecondGuardian = {
      id: 2,
      unique_guardian_number: "UGD002",
      full_name: "Jane Smith",
      status: "Documents Required",
      user: {
        mobile_number: "07123456790",
        email: "jane.smith@example.com"
      }
    };

    const mockRoom = {
      id: parseInt(roomId),
      name: "Room 101",
      price: 500.00,
      key_number: 2,
      is_couple: true
    };

    const mockPaymentData = {
      token1: "mock-token-1",
      token2: secondGuardianId ? "mock-token-2" : null,
      link1: "https://mock-payment-link-1.com",
      link2: secondGuardianId ? "https://mock-payment-link-2.com" : null,
      qrCode1: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2ZmZiIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiMwMDAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk1vY2sgUVIgQ29kZSAxPC90ZXh0Pjwvc3ZnPg==",
      qrCode2: secondGuardianId ? "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2ZmZiIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiMwMDAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk1vY2sgUVIgQ29kZSAyPC90ZXh0Pjwvc3ZnPg==" : null,
      paymentSplit: secondGuardianId ? ['50', '50'] : null
    };

    return { mockProperty, mockGuardian, mockSecondGuardian, mockRoom, mockPaymentData };
  };

  useEffect(() => {
    loadStep2Data();
  }, [propertyId, guardianId, roomId, secondGuardianId]);

  const loadStep2Data = async () => {
    try {
      setLoading(true);
      
      const url = secondGuardianId 
        ? `${process.env.REACT_APP_API_URL}/api/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}/${secondGuardianId}`
        : `${process.env.REACT_APP_API_URL}/api/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          payment_split: paymentSplit,
          second_guardian_id: secondGuardianIdForm
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('API authentication failed, using mock data');
          const { mockProperty, mockGuardian, mockSecondGuardian, mockRoom, mockPaymentData } = getMockData();
          setProperty(mockProperty);
          setGuardian(mockGuardian);
          setSecondGuardian(secondGuardianId ? mockSecondGuardian : null);
          setRoom(mockRoom);
          setPaymentData(mockPaymentData);
          setLoading(false);
          return;
        }
        
        // Handle 403 Forbidden - Role access denied
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || 'You don\'t have permission to access this page.';
          setError(errorMessage);
          toast.error(errorMessage);
          setLoading(false);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Step 2 API response:', data);
      setProperty(data.property);
      setGuardian(data.guardian);
      setSecondGuardian(data.secondGuardian);
      setRoom(data.room);
      setPaymentData(data.paymentData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading step 2 data:', err);
      // Use mock data as fallback
      const { mockProperty, mockGuardian, mockSecondGuardian, mockRoom, mockPaymentData } = getMockData();
      setProperty(mockProperty);
      setGuardian(mockGuardian);
      setSecondGuardian(secondGuardianId ? mockSecondGuardian : null);
      setRoom(mockRoom);
      setPaymentData(mockPaymentData);
      setLoading(false);
    }
  };

  const handlePaymentSplitChange = (e) => {
    setPaymentSplit(e.target.value);
  };

  const handleSecondGuardianChange = (e) => {
    setSecondGuardianIdForm(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          payment_split: paymentSplit,
          second_guardian_id: secondGuardianIdForm
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate payment tokens');
      }

      const data = await response.json();
      setPaymentData(data.paymentData);
      toast.success('Payment tokens generated successfully');
    } catch (err) {
      console.error('Error generating payment tokens:', err);
      toast.error('Failed to generate payment tokens');
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidatePayments = () => {
    const url = secondGuardian 
      ? `/guardian-placement/step3/${propertyId}/${guardianId}/${roomId}/${secondGuardian.id}`
      : `/guardian-placement/step3/${propertyId}/${guardianId}/${roomId}`;
    navigate(url);
  };

  if (loading) return <Loader />;
  
  // Display role access error prominently
  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" style={{ marginTop: '20px', padding: '20px' }}>
          <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
            <i className="glyphicon glyphicon-exclamation-sign" style={{ marginRight: '10px' }}></i>
            Access Denied
          </h4>
          <p style={{ fontSize: '16px', marginBottom: '10px' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            This page is only accessible to users with the following roles:
            <ul style={{ marginTop: '10px', marginBottom: 0 }}>
              <li>SALES WITH GUARDIANS</li>
              <li>GUARDIAN-MANAGER</li>
              <li>GUARDIAN-MANAGER-WITH-MAINTENANCE</li>
            </ul>
          </p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  if (!property || !guardian || !room) return <div className="container py-5 text-danger">Data not found</div>;

  return (
    <div className="letting-agent-dashboard">
      {/* Header Section */}
      <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
        <div className="container">
          <div className="row js-property-viewing-of-user">
            <div className="col-md-12" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
              <div className="wrapper">
                <h2 className="col-white">Placing Guardian - Step 2</h2>
                <h4 className="col-white">Payment for {property.name}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ marginTop: '20px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard">
            <div className="panel-body">
              <div className="container">
                <div className="row" style={{ marginBottom: '20px' }}>
                  <div className="col-md-12">
                    <h3>Payment Details</h3>
                    <div className="well well-sm">
                      <p><strong>Property:</strong> {property.name} ({property.property_id || property.id})</p>
                      <p><strong>Room:</strong> {room.name} - £{parseFloat(room.price || 0).toFixed(2)}/month</p>
                      <p><strong>Guardian:</strong> {guardian.full_name} ({guardian.unique_guardian_number || guardian.id})</p>
                      {secondGuardian && (
                        <p><strong>Second Guardian:</strong> {secondGuardian.full_name} ({secondGuardian.unique_guardian_number || secondGuardian.id})</p>
                      )}
                    </div>
                  </div>
                </div>

                {secondGuardian ? (
                  /* Two Guardians: Two Column Layout */
                  <div className="row">
                    <div className="col-md-6">
                      <div className="panel panel-default text-center">
                        <div className="panel-body">
                          <h4>{guardian.full_name}</h4>
                          {paymentData?.guardian1Amount && (
                            <p><strong>Amount: £{paymentData.guardian1Amount}</strong></p>
                          )}
                          {paymentData?.paymentSplit && (
                            <p className="text-muted">Payment Split: {paymentData.paymentSplit[0]}%</p>
                          )}
                          <div className="qr-code-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
                            {paymentData?.qrCode1 && (
                              <img src={paymentData.qrCode1} alt="QR Code 1" style={{ maxWidth: '300px', width: '100%' }} />
                            )}
                          </div>
                          {paymentData?.link1 && (
                            <div style={{ marginTop: '10px' }}>
                              <a href={paymentData.link1} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                Open Payment Link
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="panel panel-default text-center">
                        <div className="panel-body">
                          <h4>{secondGuardian.full_name}</h4>
                          {paymentData?.guardian2Amount && (
                            <p><strong>Amount: £{paymentData.guardian2Amount}</strong></p>
                          )}
                          {paymentData?.paymentSplit && (
                            <p className="text-muted">Payment Split: {paymentData.paymentSplit[1]}%</p>
                          )}
                          <div className="qr-code-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
                            {paymentData?.qrCode2 && (
                              <img src={paymentData.qrCode2} alt="QR Code 2" style={{ maxWidth: '300px', width: '100%' }} />
                            )}
                          </div>
                          {paymentData?.link2 && (
                            <div style={{ marginTop: '10px' }}>
                              <a href={paymentData.link2} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                Open Payment Link
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Single Guardian: Centered Layout */
                  <div className="row">
                    <div className="col-md-6 col-md-offset-3">
                      <div className="panel panel-default text-center">
                        <div className="panel-body">
                          <h4>{guardian.full_name}</h4>
                          {room?.price && (
                            <p><strong>Amount: £{parseFloat(room.price).toFixed(2)}</strong></p>
                          )}
                          <div className="qr-code-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
                            {paymentData?.qrCode1 && (
                              <img src={paymentData.qrCode1} alt="QR Code 1" style={{ maxWidth: '300px', width: '100%' }} />
                            )}
                          </div>
                          {paymentData?.link1 && (
                            <div style={{ marginTop: '10px' }}>
                              <a href={paymentData.link1} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                                Open Payment Link
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row" style={{ marginTop: '30px' }}>
                  <div className="col-md-12">
                    <div className="alert alert-info">
                      <h4><i className="fa fa-info-circle"></i> Instructions</h4>
                      <ul style={{ marginBottom: 0 }}>
                        <li>Share the QR code{secondGuardian ? 's' : ''} with the guardian{secondGuardian ? 's' : ''} or send the payment link{secondGuardian ? 's' : ''} directly</li>
                        <li>Each guardian should scan their QR code or click their payment link to complete the payment</li>
                        <li>Once payment{secondGuardian ? 's have' : ' has'} been completed, click "Validate Payments" to proceed to the next step</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="row" style={{ marginTop: '20px' }}>
                  <div className="col-md-12 text-center">
                    <button
                      className="btn btn-default btn-lg"
                      onClick={() => navigate(-1)}
                      style={{ marginRight: '10px' }}
                    >
                      <i className="fa fa-arrow-left"></i> Back
                    </button>
                    <button
                      className="btn btn-lg btn-success"
                      onClick={handleValidatePayments}
                    >
                      Validate Payments <i className="fa fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuardianPlacementStep2;












